<template>
  <div class="credits">
    <h1 class="mb-4">{{ $t('credits.title') }}</h1>
    
    <div class="row mb-4">
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">{{ $t('credits.subscription_card.title') }}</h5>
          </div>
          <div class="card-body">
            <h6 class="text-muted mb-2">{{ $t('credits.subscription_card.current_plan') }} <span class="badge bg-info">{{ userSubscriptionType }}</span></h6>
            <h2 class="display-4">{{ creditsDisplay }}</h2>
            <p v-if="userSubscriptionType === 'FREE'" class="text-muted">{{ $t('credits.subscription_card.daily_reset', { amount: userDailyLimit }) }}</p>
            <p v-if="userSubscriptionType === 'UNLIMITED'" class="text-muted">{{ $t('credits.subscription_card.unlimited') }}</p>
            <p v-if="userSubscriptionType === 'PAID_USAGE'" class="text-muted">{{ $t('credits.subscription_card.available') }}</p>
            
            <button class="btn btn-primary" @click="showPurchaseModal" v-if="userSubscriptionType !== 'UNLIMITED'">
              {{ userSubscriptionType === 'FREE' ? $t('credits.subscription_card.switch_btn') : $t('credits.subscription_card.buy_btn') }}
            </button>
          </div>
        </div>
      </div>
      
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">{{ $t('credits.info_card.title') }}</h5>
          </div>
          <div class="card-body">
            <ul class="list-group list-group-flush">
              <li class="list-group-item d-flex justify-content-between align-items-center">
                {{ $t('credits.info_card.cost') }}
                <span class="badge bg-primary rounded-pill">$0.1</span>
              </li>
              <li class="list-group-item d-flex justify-content-between align-items-center">
                {{ $t('credits.info_card.pages') }}
                <span class="badge bg-primary rounded-pill">1</span>
              </li>
              <li class="list-group-item d-flex justify-content-between align-items-center">
                {{ $t('credits.info_card.expiration') }}
                <span class="badge bg-primary rounded-pill">{{ $t('credits.info_card.never') }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">{{ $t('credits.history.title') }}</h5>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center py-3">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            
            <div v-else-if="transactions.length === 0" class="text-center py-3">
              <p>{{ $t('credits.history.no_data') }}</p>
            </div>
            
            <div v-else class="table-responsive">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>{{ $t('dashboard.table.date') }}</th>
                    <th>{{ $t('dashboard.table.type') }}</th>
                    <th>{{ $t('dashboard.table.amount') }}</th>
                    <th>{{ $t('dashboard.table.user') }}</th>
                    <th>{{ $t('dashboard.table.time') }}</th>
                    <th>{{ $t('dashboard.table.reference') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="transaction in transactions" :key="transaction.id">
                    <td>{{ formatDate(transaction.created_at) }}</td>
                    <td>
                      <span 
                        :class="transaction.transaction_type === 'purchase' ? 'badge bg-success' : 'badge bg-warning'"
                      >
                        {{ transaction.transaction_type === 'purchase' ? $t('dashboard.table.purchase') : $t('dashboard.table.usage') }}
                      </span>
                    </td>
                    <td>{{ transaction.amount }}</td>
                    <td>{{ transaction.user_email }}</td>
                    <td>{{ Math.round(transaction.processing_time || 0) }}s</td>
                    <td>{{ transaction.payment_reference }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Purchase Credits Modal -->
    <div class="modal fade" id="purchaseModal" tabindex="-1" ref="purchaseModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('credits.modal.title') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="modalError" class="alert alert-danger">{{ modalError }}</div>
            <div v-if="modalSuccess" class="alert alert-success">{{ modalSuccess }}</div>
            
            <form @submit.prevent="purchaseCredits">
              <div class="mb-3">
                <label for="creditAmount" class="form-label">{{ $t('credits.modal.amount_label') }}</label>
                <input 
                  type="number" 
                  class="form-control" 
                  id="creditAmount" 
                  v-model="creditAmount" 
                  min="1"
                  required
                >
              </div>
              
              <div class="mb-3">
                <label class="form-label">{{ $t('credits.modal.cost_label') }}</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input 
                    type="text" 
                    class="form-control" 
                    :value="(creditAmount * 0.01).toFixed(2)" 
                    readonly
                  >
                </div>
              </div>
              
              <div class="mb-3">
                <label for="paymentMethod" class="form-label">{{ $t('credits.modal.payment_method') }}</label>
                <select class="form-select" id="paymentMethod" v-model="paymentMethod">
                  <option value="credit_card">Credit Card</option>
                  <option value="paypal">PayPal</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>
              
              <!-- Simulated payment fields -->
              <div v-if="paymentMethod === 'credit_card'" class="mb-3">
                <label for="cardNumber" class="form-label">{{ $t('credits.modal.card_number') }}</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="cardNumber" 
                  placeholder="1234 5678 9012 3456"
                >
                <div class="row mt-2">
                  <div class="col-md-6">
                    <input 
                      type="text" 
                      class="form-control" 
                      placeholder="MM/YY"
                    >
                  </div>
                  <div class="col-md-6">
                    <input 
                      type="text" 
                      class="form-control" 
                      placeholder="CVC"
                    >
                  </div>
                </div>
              </div>
              
              <div class="d-grid">
                <button 
                  type="submit" 
                  class="btn btn-primary" 
                  :disabled="modalLoading || creditAmount < 1"
                >
                  <span v-if="modalLoading" class="spinner-border spinner-border-sm me-2"></span>
                  {{ $t('credits.modal.purchase_btn') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as bootstrap from 'bootstrap'

export default {
  name: 'Credits',
  data() {
    return {
      loading: false,
      modalLoading: false,
      modalError: null,
      modalSuccess: null,
      creditAmount: 100,
      paymentMethod: 'credit_card',
      modal: null
    }
  },
  computed: {
    ...mapGetters('auth', ['companyCredits', 'creditsDisplay']),
    ...mapGetters('credits', ['allTransactions']),
    
    transactions() {
      return this.allTransactions || []
    },
    userSubscriptionType() {
      return this.$store.state.auth.user ? this.$store.state.auth.user.subscription_type : ''
    },
    userDailyLimit() {
       return this.$store.state.auth.user ? (this.$store.state.auth.user.daily_free_limit || 5) : 5
    }
  },
  methods: {
    ...mapActions('credits', ['fetchTransactions', 'purchaseCredits', 'simulatePayment']),
    
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    },
    
    async loadTransactions() {
      this.loading = true
      try {
        await this.fetchTransactions()
      } catch (error) {
        console.error('Failed to fetch transactions:', error)
      } finally {
        this.loading = false
      }
    },
    
    showPurchaseModal() {
      this.modalError = null
      this.modalSuccess = null
      this.modal.show()
    },
    
    async handlePurchaseCredits() {
      this.modalLoading = true
      this.modalError = null
      this.modalSuccess = null
      
      try {
        // Generate a mock payment reference
        const paymentReference = `PAYMENT-${Date.now()}-${Math.floor(Math.random() * 1000)}`
        
        // 1. Create Invoice
        const result = await this.purchaseCredits({
          amount: this.creditAmount,
          paymentReference
        })
        
        if (result.success) {
          // 2. Simulate Payment (Since we don't have real payment gateway)
          try {
             await this.simulatePayment({ invoiceId: result.invoice.id })
             this.modalSuccess = `Successfully purchased ${this.creditAmount} credits!`
          } catch (simError) {
             console.error(simError)
             this.modalSuccess = `Invoice created #${result.invoice.reference_id}, but payment simulation failed.`
          }
          
          // Close modal after a delay
          setTimeout(() => {
            this.modal.hide()
            this.modalSuccess = null
          }, 2000)
        } else {
          this.modalError = result.error
        }
      } catch (error) {
        this.modalError = 'An unexpected error occurred'
        console.error(error)
      } finally {
        this.modalLoading = false
      }
    }
  },
  mounted() {
    this.loadTransactions()
    this.modal = new bootstrap.Modal(this.$refs.purchaseModal)
  }
}
</script>